/**
 * Filters undefined & null values in a way that is typescript safe.
 *
 * @example
 *  // expects (undefined | string)[] --> string[]
 * const arrUndefined: (void | undefined | null | string)[] = ['one', undefined, null, 'two'];
 * const arrString: string[] = arrUndefined.filter(filterUndefined); // ['one', 'two']
 */
export function filterEmpty<T>(x: T | undefined | null | void): x is T {
  return x !== undefined && x !== null;
}

/**
 * Filters falsy values in a way that is typescript safe.
 *
 * @example
 *  // expects (undefined | string)[] --> string[]
 * const arrUndefined: (void | undefined | null | false | string)[] = ['one', undefined, null, false, 'two'];
 * const arrString: string[] = arrUndefined.filter(filterUndefined); // ['one', 'two']
 */
export function filterFalsy<T>(x: T | undefined | null | void | false): x is T {
  return !!x;
}

export function setEqual(first: Set<unknown>, second: Set<unknown>): boolean {
  if (first.size !== second.size) {
    return false;
  }
  for (const item of first) {
    if (!second.has(item)) {
      return false;
    }
  }
  return true;
}

/** Shallow equality — same reference, or plain objects with `===` equal own values */
function shallowEqualObjects(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    a === null ||
    b === null
  ) {
    return false;
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every(
    key =>
      (a as Record<string, unknown>)[key] ===
      (b as Record<string, unknown>)[key]
  );
}

/**
 * Builds a per-key map with references stable across data reloads: entries
 * shallow-equal to the previous ones keep the previous value object, and when
 * every entry is unchanged the previous map itself is returned. Selectors,
 * memos and effects depending on the map (or its entries) then do not fire
 * on refetches returning equal data.
 */
export function stabilizeMapEntries<T>(
  previous: { [key: string]: T },
  next: { [key: string]: T }
): { [key: string]: T } {
  const nextKeys = Object.keys(next);
  let allSame = Object.keys(previous).length === nextKeys.length;
  const result: { [key: string]: T } = {};
  for (const key of nextKeys) {
    const previousValue = previous[key];
    if (key in previous && shallowEqualObjects(previousValue, next[key])) {
      result[key] = previousValue as T;
    } else {
      result[key] = next[key] as T;
      allSame = false;
    }
  }
  return allSame ? previous : result;
}

export function arraysEqual(a: unknown[], b: unknown[]) {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }

  // If you don't care about the order of the elements inside
  // the array, you should sort both arrays here.
  // Please note that calling sort on an array will modify that array.
  // you might want to clone your array first.

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * By using this in default switch case, we can enforce exhaustive check of all case cases.
 *
 *  switch (prop) {
 *    // ...
 *    default:
 *      throw new UnreachableCaseError(prop);
 *  }
 */
export class UnreachableCaseError extends Error {
  constructor(value: never, message?: string) {
    super(message ?? `Internal error - Unreachable case: ${value}`);
  }
}
